import {
  BuildMarketplaceContextPayloadInput,
  MARKETPLACE_ACTIONS_WITH_OFFER_CODE,
  MarketplaceContextPayload,
  PersistedAirlineRouterState
} from "./types";

const normalizeString = (value?: string | null): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = String(value).trim();

  return trimmed || null;
};

const getPersistedAirlineValue = (
  persistedAirlineState: PersistedAirlineRouterState | undefined,
  key: "iataCode" | "departureDate" | "milesAndMore"
): string | null => {
  if (persistedAirlineState?.meta?.source !== "airline") {
    return null;
  }

  return normalizeString(persistedAirlineState?.shared?.[key]);
};

/**
 * Builds the normalized `marketplace_context` dataLayer payload for both airline and hotel flows.
 *
 * Key rules:
 * - airline fields prefer direct values from the current UI state, then fall back to persisted airline router state
 * - hotel fields are only populated in hotel mode
 * - `offer_code` is included for select-offer and book actions
 * - the actual Miles & More number is never exposed; only the derived boolean flag is emitted
 * - `source` / `source_detail` come from session enrichment (wiki §6a); dwell is GA4-only (§6b)
 */
export const buildMarketplaceContextPayload = ({
  action,
  platformType,
  airline,
  hotel,
  offerCode,
  persistedAirlineState,
  session
}: BuildMarketplaceContextPayloadInput): MarketplaceContextPayload => {
  const destinationAirport =
    normalizeString(airline?.destinationAirport) ?? getPersistedAirlineValue(persistedAirlineState, "iataCode");
  const arrivalDate =
    normalizeString(airline?.arrivalDate) ?? getPersistedAirlineValue(persistedAirlineState, "departureDate");
  const milesAndMoreRaw =
    normalizeString(airline?.milesAndMoreNumber) ?? getPersistedAirlineValue(persistedAirlineState, "milesAndMore");

  const includeOfferCode = (MARKETPLACE_ACTIONS_WITH_OFFER_CODE as readonly string[]).includes(action);

  return {
    event: "marketplace_context",
    marketplace_action: action,
    platform: platformType,
    destination_airport: platformType === "airline" ? destinationAirport : null,
    arrival_date: platformType === "airline" ? arrivalDate : null,
    miles_and_more_provided: platformType === "airline" ? Boolean(milesAndMoreRaw) : false,
    hotel_id: platformType === "hotel" ? normalizeString(hotel?.id) : null,
    hotel_name: platformType === "hotel" ? normalizeString(hotel?.name) : null,
    offer_code: includeOfferCode ? normalizeString(offerCode) : null,
    type: session?.type ?? null,
    source: session?.source ?? null,
    source_detail: session?.source_detail ?? null
  };
};
